import React, { useState, useEffect } from 'react';
import { mktFetch, MARKETING_API } from './MarketingHubModal';

const API = MARKETING_API;
const PROMPT_MAX = 900;

async function parseMktJson(res) {
  const text = await res.text();
  try {
    return { data: JSON.parse(text), ok: res.ok, status: res.status };
  } catch {
    if (text.trimStart().startsWith('<')) {
      throw new Error(
        'The server returned an HTML error page (often a proxy timeout). Image generation now runs in the background — retry; if it keeps failing, wait a minute and check Studio + Review.'
      );
    }
    throw new Error(text.slice(0, 240) || `HTTP ${res.status}`);
  }
}

async function pollMediaRun(runId, onProgress) {
  const pollOnce = async (elapsed) => {
    const runRes = await mktFetch(`${API}/platform/agents/runs/${runId}`);
    const { data: runData } = await parseMktJson(runRes);
    const run = runData.run;
    const stats = typeof run?.stats === 'string' ? JSON.parse(run.stats) : (run?.stats || {});
    onProgress?.({
      elapsed,
      status: run?.status,
      phase: stats.phase,
      engine: stats.engine,
    });
    return run;
  };

  let run = await pollOnce(0);
  if (run?.status === 'completed') {
    const stats = typeof run.stats === 'string' ? JSON.parse(run.stats) : (run.stats || {});
    return stats;
  }
  if (run?.status === 'failed') throw new Error(run.error_message || 'Image generation failed');

  for (let i = 1; i < 120; i++) {
    await new Promise((r) => setTimeout(r, 3000));
    run = await pollOnce(i * 3);
    const stats = typeof run?.stats === 'string' ? JSON.parse(run.stats) : (run?.stats || {});
    if (run?.status === 'completed') return stats;
    if (run?.status === 'failed') throw new Error(run.error_message || 'Image generation failed');
  }
  throw new Error('Still generating — check Studio + Review in a minute.');
}

function phaseLabel(phase, elapsed) {
  if (phase === 'rendering') return 'Rendering image on GPU…';
  if (phase === 'loading_model') return 'Loading Qwen image model into GPU (first run after idle can take 2–3 min)…';
  if (phase === 'saving') return 'Saving image and attaching to draft…';
  if (elapsed < 90) return 'Loading Qwen image model into GPU (first run after idle can take 2–3 min)…';
  return 'Rendering image on GPU…';
}

function parseMeta(draft) {
  if (!draft) return {};
  if (typeof draft.metadata === 'string') {
    try { return JSON.parse(draft.metadata); } catch { return {}; }
  }
  return draft.metadata || {};
}

function looksTruncated(prompt) {
  if (!prompt) return false;
  const t = prompt.trim();
  return /clear for automatic$/i.test(t)
    || /clear for automatic brand$/i.test(t)
    || /clear for automatic brand logo$/i.test(t);
}

export default function MarketingGraphicPromptModal({ draft, onClose, onGenerated }) {
  const [imagePrompt, setImagePrompt] = useState('');
  const [negativePrompt, setNegativePrompt] = useState('');
  const [previewMode, setPreviewMode] = useState('');
  const [busy, setBusy] = useState('');
  const [err, setErr] = useState('');
  const [cacheNote, setCacheNote] = useState('');
  const [progress, setProgress] = useState('');
  const [elapsed, setElapsed] = useState(0);

  useEffect(() => {
    if (busy !== 'generating') {
      setElapsed(0);
      return undefined;
    }
    const t0 = Date.now();
    const tick = setInterval(() => setElapsed(Math.floor((Date.now() - t0) / 1000)), 1000);
    return () => clearInterval(tick);
  }, [busy]);

  useEffect(() => {
    if (!draft?.id) return;
    let cancelled = false;

    const meta = parseMeta(draft);
    const cached = meta.graphic_draft || meta.graphic;
    if (cached?.image_prompt && !looksTruncated(cached.image_prompt)) {
      setImagePrompt(cached.image_prompt);
      setNegativePrompt(cached.negative_prompt || '');
      setPreviewMode(cached.preview_mode || '');
      setCacheNote(cached.image_prompt ? 'Loaded saved prompt — click REDRAFT PROMPT for a fresh Ollama draft.' : '');
      return undefined;
    }

    (async () => {
      setBusy('drafting');
      setErr('');
      setCacheNote(looksTruncated(cached?.image_prompt) ? 'Previous prompt was incomplete — redrafting…' : '');
      try {
        const res = await mktFetch(`${API}/platform/drafts/${draft.id}/draft-graphic-prompt`, {
          method: 'POST',
          body: JSON.stringify({ refresh: !!looksTruncated(cached?.image_prompt) }),
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || 'Could not draft prompt');
        if (cancelled) return;
        setImagePrompt(data.image_prompt || '');
        setNegativePrompt(data.negative_prompt || '');
        setPreviewMode(data.preview_mode || '');
        setCacheNote('');
      } catch (e) {
        if (!cancelled) setErr(e.message);
      } finally {
        if (!cancelled) setBusy('');
      }
    })();

    return () => { cancelled = true; };
  }, [draft?.id]);

  if (!draft) return null;

  const generateImage = async () => {
    if (!imagePrompt.trim()) {
      setErr('Image prompt cannot be empty.');
      return;
    }
    setBusy('generating');
    setErr('');
    setProgress('Starting image generation…');
    try {
      const res = await mktFetch(`${API}/platform/drafts/${draft.id}/generate-media`, {
        method: 'POST',
        body: JSON.stringify({
          kind: 'graphic',
          image_prompt: imagePrompt.trim(),
          negative_prompt: negativePrompt.trim(),
        }),
      });
      const { data, ok } = await parseMktJson(res);
      if (!ok) throw new Error(data.error || 'Generation failed');
      let result = data;
      if (data.run_id && (data.started || data.already_running)) {
        setProgress(data.message || 'Generating image…');
        result = await pollMediaRun(data.run_id, ({ elapsed: sec, phase, engine }) => {
          const label = phaseLabel(phase, sec);
          setProgress(`${label} ${sec}s${engine ? ` · ${engine}` : ''}`);
        });
      }
      onGenerated?.(result);
      onClose();
    } catch (e) {
      setErr(e.message);
    } finally {
      setBusy('');
      setProgress('');
    }
  };

  const redraftPrompt = async () => {
    setBusy('drafting');
    setErr('');
    setCacheNote('');
    try {
      const res = await mktFetch(`${API}/platform/drafts/${draft.id}/draft-graphic-prompt`, {
        method: 'POST',
        body: JSON.stringify({ refresh: true }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Redraft failed');
      setImagePrompt(data.image_prompt || '');
      setNegativePrompt(data.negative_prompt || '');
      setPreviewMode(data.preview_mode || '');
    } catch (e) {
      setErr(e.message);
    } finally {
      setBusy('');
    }
  };

  return (
    <div
      className="mkt-preview-backdrop"
      role="presentation"
      onClick={busy ? undefined : onClose}
    >
      <div className="mkt-preview-modal mkt-graphic-prompt-modal" role="dialog" onClick={(e) => e.stopPropagation()}>
        <div className="mkt-preview-modal-head">
          <h4>Graphic prompt · {draft.platform}{previewMode ? ` · ${previewMode}` : ''}</h4>
          <button
            type="button"
            className="blog-tactical-action-btn blog-tactical-action-delete"
            disabled={busy === 'generating'}
            onClick={onClose}
          >
            CLOSE
          </button>
        </div>
        <p className="marketing-panel-desc">
          Edit the prompt, then generate. If this identity has a logo (Identities → Edit rule-set), it is composited bottom-right after generation — do not ask the AI to draw a logo.
        </p>
        {busy === 'generating' && (
          <div className="mkt-graphic-generating-banner" role="status" aria-live="polite">
            <span className="mkt-graphic-spinner" aria-hidden="true" />
            <div className="mkt-graphic-generating-text">
              <strong>{progress || 'GENERATING IMAGE…'}</strong>
              <span className="marketing-source-meta">
                Qwen image model · {elapsed}s elapsed · keep this dialog open
              </span>
            </div>
          </div>
        )}
        {busy === 'drafting' && <p className="marketing-msg">Drafting prompt with Ollama…</p>}
        {cacheNote && !busy && <p className="marketing-source-meta">{cacheNote}</p>}
        <label className="marketing-integration-label">
          Image prompt
          <span className="marketing-prompt-char-count">{imagePrompt.length} / {PROMPT_MAX}</span>
          <textarea
            rows={8}
            value={imagePrompt}
            disabled={busy === 'drafting'}
            onChange={(e) => setImagePrompt(e.target.value.slice(0, PROMPT_MAX))}
          />
        </label>
        <label className="marketing-integration-label">
          Negative prompt
          <textarea
            rows={3}
            value={negativePrompt}
            disabled={busy === 'drafting'}
            onChange={(e) => setNegativePrompt(e.target.value)}
          />
        </label>
        {progress && busy === 'generating' && <p className="marketing-msg mkt-graphic-progress-detail">{progress}</p>}
        {err && <p className="marketing-msg-err">{err}</p>}
        <div className="marketing-form-actions">
          <button
            type="button"
            className="blog-tactical-action-btn blog-tactical-action-edit"
            disabled={!!busy}
            onClick={generateImage}
          >
            {busy === 'generating' ? (progress || 'GENERATING…') : 'GENERATE IMAGE'}
          </button>
          <button
            type="button"
            className="blog-tactical-action-btn blog-tactical-action-view"
            disabled={!!busy}
            onClick={redraftPrompt}
          >
            REDRAFT PROMPT
          </button>
        </div>
      </div>
    </div>
  );
}
